import { Formik } from 'formik';
import React from 'react';
import {
  Container,
  Divider,
  Form,
  Header,
  Button,
  Message,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'types/provider';
import { request } from 'utils/api';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import InputField from 'components/form-fields/formik/InputField';
import { formatDuration, intervalToDuration } from 'date-fns';
import styles from './Settings.module.css';

const SettingsPlatformAccess: React.FC<{
  provider: Partial<Provider>;
}> = ({ provider }) => {
  const { t } = useTranslation();
  return (
    <Container>
      <Header as="h2" style={{ padding: '0', margin: '0' }}>
        {t('editSandboxPlatformAccess.title', 'Platform Access')}
      </Header>
      <Divider />
      <p>
        {t('editSandboxPlatformAccess.description', 'Platform access settings')}
      </p>
      <Formik
        enableReinitialize
        initialValues={{
          enableSelfSignup: provider.enableSelfSignup,
          enableSocialLogin: provider.enableSocialLogin,
          enableSingleSignOn: provider.enableSingleSignOn,
          ignoreJwtTokenExpiration: provider.ignoreJwtTokenExpiration,
          jwtTokenExpirationSeconds: provider.jwtTokenExpirationSeconds,
        }}
        onSubmit={(values) => {
          request({
            method: 'PATCH',
            path: `/1/sandboxes/${(provider as any)._id}/settings`,
            body: {
              ...provider,
              ...values,
            },
          });
        }}>
        {({ handleSubmit, values, isSubmitting, isValid, dirty }) => {
          return (
            <Form>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxPlatformAccess.enableSelfSignup',
                    'Enable self-signup'
                  )}
                  name="enableSelfSignup"
                />
                <Message info>
                  {t(
                    'editSandboxPlatformAccess.enableSelfSignupDescription',
                    'Allows users to sign up for an account using their email and password.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxPlatformAccess.enableSocialLogin',
                    'Enable Social Login'
                  )}
                  name="enableSocialLogin"
                />
                <Message info>
                  {t(
                    'editSandboxPlatformAccess.socialLoginDescription',
                    'Allows users to authenticate using their social media accounts (Google, Facebook, etc.) for faster registration and login.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxPlatformAccess.enableSingleSignOn',
                    'Enable Single Sign On (alpha, not for production use)'
                  )}
                  name="enableSingleSignOn"
                />
                <Message info>
                  {t(
                    'editSandboxPlatformAccess.enableSingleSignOnDescription',
                    'Alpha feature for SSO integration allowing users to authenticate using existing corporate or social media accounts.'
                  )}
                </Message>
              </Form.Field>
              <Message info>
                <Message.Header>
                  {t(
                    'editSandboxPlatformAccess.userSessionSettigns',
                    'User session configuration'
                  )}
                </Message.Header>
                <Message.Content>
                  <br />
                  {t(
                    'editSandboxPlatformAccess.userSessionSettignsDescription',
                    'The below section controls the security settings pertaining to logons with username and password alone. The defaults have been picked as a result of security audit, and should not be changed unless consulted with the Product/Engineering department.'
                  )}
                  <br />
                  <br />
                  {t(
                    'editSandboxPlatformAccess.userSessionSettignsDescription2',
                    'With API Token/Session expiration disabled, once signed in, the user will remain logged in until they sign out or change browsers. This is almost never desirable.'
                  )}
                  <br />
                  <br />
                  {t(
                    'editSandboxPlatformAccess.userSessionSettignsDescription3',
                    'The Session expiration time to live (TTL) expresses the time in seconds until the user is required to sign in again.'
                  )}
                </Message.Content>
              </Message>

              <CheckboxField
                label={t(
                  'editSandboxPlatformAccess.ignoreJwtTokenExpiration',
                  'Ignore JWT Token Expiration'
                )}
                name="ignoreJwtTokenExpiration"
              />

              <InputField
                label={t(
                  'editSandboxPlatformAccess.jwtTokenExpirationSeconds',
                  `Session expiration TTL (seconds)`
                )}
                name="jwtTokenExpirationSeconds"
                disabled={values.ignoreJwtTokenExpiration}
                type={'number'}
              />

              {!values.ignoreJwtTokenExpiration &&
                values.jwtTokenExpirationSeconds && (
                  <span>
                    {t(
                      'editSandboxPlatformAccess.jwtTokenExpirationSecondsDescription',
                      'which translates to:'
                    )}
                    {formatDuration(
                      intervalToDuration({
                        start: 0,
                        end: values.jwtTokenExpirationSeconds * 1000,
                      })
                    )}
                  </span>
                )}
              <br />
              <Button
                disabled={isSubmitting || !isValid}
                onClick={() => handleSubmit()}
                primary>
                {t('save', 'Save')}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default SettingsPlatformAccess;
